import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Icon from './Icon';
import { fetchWaVoteInfo, submitWaVote } from '../waVoteApi';
import { useLanguage } from '../i18n/language';
import './BranchesPage.css';

function voteLabel(v, isAr) {
  if (v === 'approve') return isAr ? 'موافق' : 'Approve';
  if (v === 'reject') return isAr ? 'رفض' : 'Reject';
  if (v === 'abstain') return isAr ? 'امتناع' : 'Abstain';
  return v || '—';
}

export default function WhatsAppVotePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const t = useMemo(() => ({
    title: isAr ? 'تصويت على قرار' : 'Decision vote',
    loading: isAr ? 'جارٍ التحميل…' : 'Loading…',
    noToken: isAr ? 'الرابط غير صالح (ناقص t).' : 'Invalid link (missing token).',
    company: isAr ? 'الشركة' : 'Company',
    deadline: isAr ? 'الموعد النهائي' : 'Deadline',
    you: isAr ? 'التصويت كـ' : 'Voting as',
    pick: isAr ? 'اختر موقفك' : 'Choose your vote',
    saved: isAr ? 'تم حفظ التصويت.' : 'Your vote was saved.',
    sendError: isAr ? 'تعذر الإرسال' : 'Could not submit',
    statusPending: isAr ? 'معلّق' : 'Pending',
    statusApproved: isAr ? 'معتمد' : 'Approved',
    statusRejected: isAr ? 'مرفوض' : 'Rejected',
    closed: isAr ? 'التصويت مغلق لهذا القرار.' : 'Voting is closed for this decision.',
    deadlineOver: isAr ? 'انتهى الموعد النهائي للتصويت.' : 'The voting deadline has passed.',
    lblStatus: isAr ? 'حالة القرار' : 'Decision status',
  }), [isAr]);

  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError(t.noToken);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const d = await fetchWaVoteInfo(token);
        if (!cancelled) setInfo(d);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t.sendError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, t.noToken, t.sendError]);

  const statusText = (s) => {
    if (s === 'approved') return t.statusApproved;
    if (s === 'rejected') return t.statusRejected;
    return t.statusPending;
  };

  const handleVote = async (choice) => {
    setSubmitError('');
    setSubmitting(true);
    try {
      const d = await submitWaVote(token, choice);
      setInfo(d);
      setDone(true);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : t.sendError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div dir="rtl" lang="ar" style={{
      minHeight: '100vh',
      background: 'linear-gradient(165deg, #e8f8f5 0%, #f5fbff 45%, #ffffff 100%)',
      fontFamily: '"Cairo", "Segoe UI", sans-serif',
      padding: '24px 16px',
    }}
    >
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, color: '#00334a' }}>
          <Icon name="decisions" size={26} color="#02c3a6" />
          <span style={{ fontSize: '1.25rem', fontWeight: 800 }}>{t.title}</span>
        </div>

        {loading && <div style={{ textAlign: 'center', color: '#78909c', padding: 32 }}>{t.loading}</div>}
        {loadError && (
          <div style={{ padding: 16, background: '#ffebee', color: '#b71c1c', borderRadius: 12 }}>
            {loadError}
          </div>
        )}

        {!loading && !loadError && info && (
          <div className="table-card" style={{ padding: '22px 20px', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,51,74,0.08)' }}>
            <h1 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#00334a', margin: '0 0 8px', lineHeight: 1.4 }}>
              {info.title}
            </h1>
            <p style={{ margin: '0 0 6px', color: '#546e7a', fontSize: '0.9rem' }}>
              <strong>{t.company}:</strong> {info.companyName}
            </p>
            {info.deadline && (
              <p style={{ margin: '0 0 6px', color: '#546e7a', fontSize: '0.9rem', direction: 'ltr', textAlign: 'inherit' }}>
                <strong>{t.deadline}:</strong> {info.deadline}
              </p>
            )}
            <p style={{ margin: '0 0 16px', color: '#78909c', fontSize: '0.85rem' }}>
              {t.you}: {info.recipientLabel}
            </p>
            <p style={{ margin: '0 0 16px', fontSize: '0.88rem', color: '#37474f' }}>
              <strong>{t.lblStatus}:</strong>{' '}
              <span style={{ fontWeight: 700 }}>{statusText(info.status)}</span>
            </p>

            {info.status !== 'pending' && (
              <div style={{ padding: 12, background: '#fff3e0', borderRadius: 10, color: '#e65100', fontSize: '0.9rem' }}>{t.closed}</div>
            )}

            {info.status === 'pending' && !info.canVote && !done && (
              <div style={{ padding: 12, background: '#fff3e0', borderRadius: 10, color: '#e65100', fontSize: '0.9rem' }}>{t.deadlineOver}</div>
            )}

            {info.myVote && (
              <p style={{ margin: '12px 0', color: '#00695c', fontWeight: 700 }}>
                {isAr ? 'تصويتك الحالي:' : 'Current vote:'}{' '}
                {voteLabel(info.myVote, isAr)}
              </p>
            )}

            {info.canVote && !done && (
              <div>
                <div style={{ fontWeight: 700, color: '#00334a', marginBottom: 12 }}>{t.pick}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button type="button" className="btn btn--primary" disabled={submitting} onClick={() => handleVote('approve')} style={{ width: '100%', justifyContent: 'center' }}>
                    {voteLabel('approve', isAr)}
                  </button>
                  <button type="button" className="btn" disabled={submitting} onClick={() => handleVote('reject')} style={{ width: '100%', justifyContent: 'center', background: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2' }}>
                    {voteLabel('reject', isAr)}
                  </button>
                  <button type="button" className="btn btn--ghost" disabled={submitting} onClick={() => handleVote('abstain')} style={{ width: '100%', justifyContent: 'center' }}>
                    {voteLabel('abstain', isAr)}
                  </button>
                </div>
              </div>
            )}

            {done && (
              <div style={{ marginTop: 16, padding: 14, background: '#e0f2f1', color: '#00695c', borderRadius: 10, fontWeight: 700 }}>
                {t.saved}
              </div>
            )}

            {submitError && (
              <div style={{ marginTop: 12, padding: 12, background: '#ffebee', color: '#b71c1c', borderRadius: 10, fontSize: '0.9rem' }}>
                {submitError}
              </div>
            )}
          </div>
        )}

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: '0.8rem', color: '#90a4ae' }}>Corp Pass</p>
      </div>
    </div>
  );
}
